/**
 * Core error types and throwing error utilities.
 *
 * Unlike CLI helpers which call process.exit(1), these throw typed errors
 * that consumers (CLI, TUI) can catch and handle in their own way.
 */

export class ArbiError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ArbiError'
  }
}

export class ArbiApiError extends ArbiError {
  public readonly apiError?: unknown

  constructor(message: string, apiError?: unknown) {
    super(message)
    this.name = 'ArbiApiError'
    this.apiError = apiError
  }
}

/**
 * Check API response and throw on error. Returns narrowed non-null data.
 */
export function requireData<T>(result: { data?: T; error?: unknown }, message: string): T {
  if (result.error || !result.data) {
    throw new ArbiApiError(message, result.error)
  }
  return result.data
}

/**
 * Check API response for delete/void operations (no data expected).
 */
export function requireOk(result: { error?: unknown }, message: string): void {
  if (result.error) {
    throw new ArbiApiError(message, result.error)
  }
}

/**
 * Extract a human-readable message from an unknown error value.
 * Centralises the `err instanceof Error ? err.message : String(err)` pattern.
 *
 * Unwraps `.cause` chains to surface the root error — critical for Node.js
 * fetch errors where TypeError("fetch failed") wraps the real cause
 * (ECONNREFUSED, UNABLE_TO_VERIFY_LEAF_SIGNATURE, etc.).
 */
export function getErrorMessage(err: unknown): string {
  if (!(err instanceof Error)) return String(err)

  // Walk the cause chain to find the deepest meaningful error
  const rootCause = getDeepestCause(err)
  if (rootCause !== err) {
    const rootMsg = rootCause.message || String(rootCause)
    // Include both the top-level context and root cause
    if (err.message && !err.message.includes(rootMsg)) {
      return `${err.message}: ${rootMsg}`
    }
  }

  return err.message
}

/**
 * Walk the .cause chain to find the deepest Error.
 */
function getDeepestCause(err: Error): Error {
  let current: Error = err
  const seen = new Set<Error>()
  while (current.cause instanceof Error && !seen.has(current.cause)) {
    seen.add(current)
    current = current.cause
  }
  return current
}

/**
 * Extract the Node.js error code (e.g. 'ECONNREFUSED', 'ENOTFOUND') from an
 * error or its cause chain. Returns undefined if no code is found.
 */
export function getErrorCode(err: unknown): string | undefined {
  if (!(err instanceof Error)) return undefined

  let current: Error | undefined = err
  const seen = new Set<Error>()
  while (current && !seen.has(current)) {
    seen.add(current)
    const code = (current as Error & { code?: string }).code
    if (typeof code === 'string') return code
    current = current.cause instanceof Error ? current.cause : undefined
  }
  return undefined
}
